import {
    Card,
    CardHeading,
    Icon,
    IconWithVariant
} from '@auviknetworks/iceberg-ui';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    Collapse,
    FormControlLabel,
    Grid,
    Paper,
    Radio,
    RadioGroup,
    TableCell,
    TableRow,
    Typography
} from '@mui/material';
import { removeBasename } from 'application/Home/Admin/Users/ProviderGroups';
import { ManagedTable } from 'application/Shared/Components/Table';
import { DarkTooltip } from 'application/Shared/Components/Tooltips';
import { FontSizes } from 'application/Shared/Constants';
import { Helpers } from 'application/Shared/Helpers';
import {
    useFetchProviderGroups,
    useFetchProviderGroupsUsers
} from 'hooks/dataAccess/Provider/Groups/tenantProviderGroups';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const SelectAllHeaderCheckbox = ({ selectAll, setSelectAll }) => (
    <FormControlLabel
        key={`selectAllHeaderCheckbox${selectAll}`}
        control={
            <Checkbox
                onChange={(e) => {
                    if (e.target.checked) {
                        setSelectAll(true);
                    } else {
                        setSelectAll(false);
                    }
                }}
            />
        }
        sx={{
            '& .MuiFormControlLabel-label': {
                display: 'inline-flex'
            },
            '&.MuiFormControlLabel-root': {
                marginLeft: 0
            }
        }}
    />
);

/**
 * The ChooseGroups component is responsible for rendering a UI for selecting groups to exclude
 * from a provider (Google Workspace or Microsoft Entra ID).
 *
 * @param accessToken - (Optional) The access token required to authenticate with the provider.
 * @param applicationId - (Optional) The client id for the Microsoft Entra ID Tenant.
 * @param applicationSecret - (Optional) The client secret for the Microsoft Entra ID Tenant.
 * @param formFooterComponent - (Optional) An optional component to be rendered in the form footer.
 * @param groups - An object representing the selected groups to be excluded.
 * @param identityTenantId - (Optional) The ID of the Microsoft Entra ID Tenant.
 * @param integrationType - The type of integration. Values can be 'googleAutomated', 'microsoftEntraIDAutomated or 'microsoftEntraIDManual'.
 * @param linkedTenantId - The ID of the linked tenant.
 * @param setGroups - A function to set the selected groups.
 * @param useAsmAppCredentials - (Optional) A boolean indicating whether to use ASM app credentials.
 */
export const ChooseGroups = ({
    accessToken,
    applicationId,
    applicationSecret,
    formFooterComponent,
    groups,
    identityTenantId,
    integrationType,
    linkedTenantId,
    setGroups,
    useAsmAppCredentials
}) => {
    const { t } = useTranslation();
    const [providerRoute, setProviderRoute] = useState(null);
    const [scanOption, setScanOption] = useState('all');
    const [selectedRow, setSelectedRow] = useState({});
    const [groupUsers, setGroupUsers] = useState([]);
    const [rowId, setRowId] = useState(null);
    const [selectAll, setSelectAll] = useState(false);

    const { providerGroups, loading } = useFetchProviderGroups({
        accessToken,
        applicationId,
        applicationSecret,
        clientId: linkedTenantId,
        identityTenantId,
        providerRoute,
        useAsmAppCredentials
    });

    const { providerGroupsUsers, loading: loadingUsers } =
        useFetchProviderGroupsUsers({
            accessToken,
            applicationId,
            applicationSecret,
            clientId: linkedTenantId,
            id: rowId,
            identityTenantId,
            isOrgUnit: selectedRow.isOrgUnit,
            providerRoute,
            useAsmAppCredentials
        });

    useEffect(() => {
        if (integrationType === 'googleAutomated') {
            setProviderRoute('google');
        } else {
            setProviderRoute('azure');
        }
    }, [integrationType]);

    useEffect(() => {
        if (selectedRow) {
            const { id, isOrgUnit, displayName } = selectedRow;

            setRowId(
                isOrgUnit
                    ? removeBasename(displayName).replaceAll('/', '%2F')
                    : id
            );
        }
    }, [selectedRow]);

    useEffect(() => {
        if (!loadingUsers) {
            setGroupUsers(providerGroupsUsers);
        }
    }, [loadingUsers, providerGroupsUsers]);

    useEffect(() => {
        if (selectAll) {
            setGroups(
                providerGroups.reduce((acc, val) => {
                    acc[val.id] = val;
                    return acc;
                }, {})
            );
        } else {
            setGroups({});
        }
    }, [selectAll, providerGroups, setGroups]);

    const provider =
        integrationType === 'googleAutomated'
            ? t('Common.google_workspace')
            : t('Common.microsoftEntraID');

    const groupsUsersHeaders = [
        {
            align: 'left',
            field: 'users',
            renderHeader: t('Common.users'),
            width: '40%'
        },
        {
            align: 'left',
            field: 'providerUserId',
            renderHeader: t('Common.userId'),
            width: '58%'
        }
    ];

    const renderGroupName = (row) => {
        const { displayName, isOrgUnit } = row;
        const actualDisplayName = displayName.split('/').at(-1);
        return (
            <Grid container alignItems="center">
                <Grid item xs={5} data-testid="chooseGroupsTableName">
                    {actualDisplayName}
                    <br />
                    <small>{row.id}</small>
                </Grid>
                <Grid item xs={6}>
                    {isOrgUnit ? (
                        <Chip
                            data-testid="chooseGroupsTableChip"
                            label={
                                <Typography
                                    fontSize={FontSizes[0]}
                                    dangerouslySetInnerHTML={{
                                        __html: t('Common.org_unit_bold', {
                                            orgUnit: displayName
                                                .split('/')
                                                .slice(0, -1)
                                                .join('/')
                                        })
                                    }}
                                    sx={{ color: 'providerGroup.orgUnit.text' }}
                                />
                            }
                            size="small"
                            sx={{
                                backgroundColor:
                                    'providerGroup.orgUnit.backgroundColor',
                                border: '1px solid',
                                borderColor: 'providerGroup.orgUnit.borderColor'
                            }}
                        />
                    ) : (
                        <Chip
                            data-testid="chooseGroupsTableChip"
                            label={
                                <Typography
                                    fontSize={FontSizes[0]}
                                    sx={{ color: 'providerGroup.default.text' }}
                                >
                                    {t('Common.group')}
                                </Typography>
                            }
                            size="small"
                            sx={{
                                backgroundColor:
                                    'providerGroup.default.backgroundColor',
                                border: '1px solid',
                                borderColor: 'providerGroup.default.borderColor'
                            }}
                        />
                    )}
                </Grid>
                <Grid item xs={1}>
                    {row.excluded && (
                        <DarkTooltip
                            arrow
                            placement="bottom"
                            title={t('Tooltips.provider.excludeGroup')}
                        >
                            <Box>
                                <IconWithVariant
                                    variant="danger"
                                    src="BanCircle"
                                    size={FontSizes[2]}
                                />
                            </Box>
                        </DarkTooltip>
                    )}
                </Grid>
            </Grid>
        );
    };

    const renderGroupsUsersRow = (row) => (
        <TableRow
            data-testid="userGroupsRow"
            sx={{ backgroundColor: 'table.rowBackground' }}
        >
            <TableCell width="40%">{row.displayName}</TableCell>
            <TableCell width="30%">
                {row.serialId ? row.tenantUserId : row.id}
            </TableCell>
        </TableRow>
    );

    const groupsHeaders = [
        {
            align: 'left',
            field: 'edit',
            renderHeader: (
                <SelectAllHeaderCheckbox
                    selectAll={selectAll}
                    setSelectAll={setSelectAll}
                />
            ),
            width: '2%'
        },
        {
            align: 'left',
            field: 'name',
            renderHeader: t('Common.name'),
            width: '40%'
        },
        {
            align: 'left',
            field: 'description',
            renderHeader: t('Common.description'),
            width: '30%'
        },
        {
            align: 'left',
            field: 'users',
            renderHeader: t('Common.users'),
            width: '13%'
        }
    ];

    const renderGroupsUsersTable = () => (
        <Paper
            data-testid="userGroupTable"
            square
            sx={{ boxShadow: '0px 4px 10px 0px #00000026 inset' }}
        >
            <ManagedTable
                headers={groupsUsersHeaders}
                externalLoading={loadingUsers}
                manageRows
                renderBody={renderGroupsUsersRow}
                rows={groupUsers}
                searchEnabled
                searchFields={['displayName']}
                renderFooter
            />
        </Paper>
    );

    const renderGroupsRow = (row) => {
        const { excluded, id } = row;
        const checked = Helpers.isset(groups, id);
        return (
            <>
                <TableRow
                    data-testid="providerGroupsRow"
                    sx={{ backgroundColor: 'table.rowBackground' }}
                >
                    <TableCell style={{ textAlign: 'left' }}>
                        {!excluded && (
                            <Checkbox
                                key={`checkbox${id}_${selectAll}`}
                                checked={checked ?? false}
                                onClick={(e) => {
                                    const localCheckedMap = { ...groups };
                                    if (e.target.checked) {
                                        localCheckedMap[id] = row;
                                    } else {
                                        delete localCheckedMap[id];
                                    }
                                    setGroups(localCheckedMap);
                                }}
                            />
                        )}
                    </TableCell>
                    <TableCell width="40%">{renderGroupName(row)}</TableCell>
                    <TableCell width="30%">{row.description}</TableCell>
                    <TableCell>
                        <Button
                            onClick={() => {
                                setGroupUsers([]);
                                if (id === selectedRow.id) setSelectedRow({});
                                else setSelectedRow(row);
                            }}
                            variant="outlined"
                            color="primary"
                            data-testid={`usersExpandButton_${id}`}
                            data-trackid={
                                selectedRow.id === row.id
                                    ? 'client_chooseGroups_usersCollapse'
                                    : 'client_chooseGroups_usersExpand'
                            }
                        >
                            <IconWithVariant
                                variant="inherit"
                                src="Users"
                                size={FontSizes[2]}
                                wrapperProps={{
                                    marginRight: '4px'
                                }}
                            />
                            <Icon
                                size={FontSizes[1]}
                                src={
                                    selectedRow.id === row.id
                                        ? 'ChevronDown'
                                        : 'ChevronRight'
                                }
                            />
                        </Button>
                    </TableCell>
                </TableRow>
                <TableRow variant="noHover">
                    <TableCell
                        sx={{
                            padding: 0
                        }}
                        colSpan={6}
                    >
                        <Collapse
                            in={selectedRow.id === row.id}
                            timeout="auto"
                            unmountOnExit
                        >
                            {renderGroupsUsersTable()}
                        </Collapse>
                    </TableCell>
                </TableRow>
            </>
        );
    };

    const renderGroupsTable = () => {
        const groupCount = Object.keys(groups).length;

        return (
            <Card sx={{ padding: '8px' }} data-testid="chooseGroupsTable">
                <CardHeading count={groupCount} p="16px" pt="8px">
                    <Typography variant="span" fontSize={FontSizes[3]}>
                        {t(
                            'Onboarding.quickScanSelectGroups.excludeSpecificGroups.selected',
                            { count: groupCount }
                        )}
                    </Typography>
                </CardHeading>
                <ManagedTable
                    headers={groupsHeaders}
                    externalLoading={loading}
                    manageRows
                    renderBody={renderGroupsRow}
                    rows={providerGroups}
                    renderFooter
                />
            </Card>
        );
    };

    return (
        <Box
            display="flex"
            flexDirection="column"
            height="100%"
            justifyContent="space-between"
        >
            <Box p="24px" m="0" width="100%">
                <Typography
                    component="span"
                    dangerouslySetInnerHTML={{
                        __html: t('Clients.addModal.steps.5', { provider })
                    }}
                />
                <Box mt="24px">
                    <RadioGroup
                        row
                        aria-label="userOrGroupSelection"
                        name="userOrGroupSelection"
                        value={scanOption}
                        onChange={(e, v) => {
                            if (v === 'all') {
                                setGroups({});
                                setSelectAll(false);
                            }
                            setScanOption(v);
                        }}
                    >
                        <>
                            <FormControlLabel
                                data-testid="scanAllGroups"
                                value="all"
                                control={<Radio color="primary" />}
                                label={t(
                                    'Onboarding.quickScanSelectGroups.scanAllGroups'
                                )}
                            />
                            <FormControlLabel
                                data-testid="excludeSpecificGroups"
                                value="exclude"
                                control={<Radio color="primary" />}
                                label={t(
                                    'Onboarding.quickScanSelectGroups.excludeSpecificGroups'
                                )}
                            />
                        </>
                    </RadioGroup>
                </Box>
                {scanOption === 'exclude' && (
                    <>
                        <Box mt="32px" mb="32px">
                            <Alert severity="warning" icon={false}>
                                {t(
                                    'Onboarding.quickScanSelectGroups.excludeSpecificGroups.message'
                                )}
                            </Alert>
                        </Box>
                        {renderGroupsTable()}
                    </>
                )}
            </Box>
            {formFooterComponent}
        </Box>
    );
};
